// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip
} from '@mui/material'
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material'
import { loadFkspRequests, updateFkspRequestStatus, downloadFkspPdf, deleteFkspRequest } from '../lib/apiPhpAdapter'
import { FkspRequest } from '../types'
import { format } from 'date-fns'
import { FkspRequestModal } from '../components/FkspRequestModal'
import { FkspRequestsToolbar } from '../components/FkspRequestsToolbar'
import { FkspTablePagination, fkspPageSlice } from '../components/FkspTablePagination'
import { filterAndSortFkspRequests } from '../lib/fkspListFilters'
import type { FkspEkonomSortField } from '../lib/fkspConstants'
import { FKSP_LIST_FETCH_LIMIT } from '../lib/fkspConstants'
import { fkspRowSxIfEconomProcessed } from '../lib/fkspStatusLabels'
import { useAuth } from '../hooks/useAuth'

export const FkspDirectorView: React.FC = () => {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandled = useRef<string | null>(null)
  const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'
  const [requests, setRequests] = useState<FkspRequest[]>([])
  const [toast, setToast] = useState<{show: boolean, msg: string, type: 'success'|'error'}>({show: false, msg: '', type: 'success'})
  const [listLoaded, setListLoaded] = useState(false)

  const [selectedReq, setSelectedReq] = useState<FkspRequest | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null) // 'approve' | 'reject' | null
  const [listSearch, setListSearch] = useState('')
  const [listCreatedFrom, setListCreatedFrom] = useState('')
  const [listCreatedTo, setListCreatedTo] = useState('')
  const [listSortField, setListSortField] = useState<FkspEkonomSortField>('created_at')
  const [listDateNewestFirst, setListDateNewestFirst] = useState(true)
  const [pendingPage, setPendingPage] = useState(0)
  const [historyPage, setHistoryPage] = useState(0)

  const fkspFilterParams = useMemo(
    () => ({
      searchQuery: listSearch,
      dateNewestFirst: listDateNewestFirst,
      createdFrom: listCreatedFrom,
      createdTo: listCreatedTo,
      sortField: listSortField,
    }),
    [listSearch, listDateNewestFirst, listCreatedFrom, listCreatedTo, listSortField],
  )

  useEffect(() => {
    fetchRequests()
  }, [])

  const fetchRequests = async () => {
    try {
      const data = await loadFkspRequests('director', { limit: FKSP_LIST_FETCH_LIMIT })
      setRequests(data)
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    } finally {
      setListLoaded(true)
    }
  }

  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || !listLoaded) return
    if (deepLinkHandled.current === id) return
    deepLinkHandled.current = id

    const found = requests.find((r) => r.id === id)
    if (found) {
      setSelectedReq(found)
    } else {
      setToast({
        show: true,
        msg: 'Žádost nenalezena nebo již byla zpracována.',
        type: 'error',
      })
    }
    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next, { replace: true })
  }, [searchParams, listLoaded, requests, setSearchParams])

  const handleUpdateStatus = async (requestId: string, action: 'approve' | 'reject', note: string) => {
    setActionLoading(action)
    try {
      await updateFkspRequestStatus(requestId, 'director', action, note)
      setToast({ show: true, msg: 'Stav byl úspěšně změněn', type: 'success' })
      fetchRequests()
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  const handleDownloadPdf = async (req: FkspRequest) => {
    try {
      setToast({ show: true, msg: 'Generuji PDF...', type: 'success' })
      await downloadFkspPdf(req.id, req.type)
      setToast({ show: true, msg: 'PDF bylo staženo', type: 'success' })
    } catch (err: any) {
      setToast({ show: true, msg: 'Chyba při stahování PDF: ' + err.message, type: 'error' })
    }
  }

  // U ředitele, čekající jsou buď zocmos_approved nebo zocmos_rejected nebo director_pending
  const pendingRequests = requests.filter(r =>
    r.status === 'director_pending' ||
    r.status === 'zocmos_approved' ||
    r.status === 'zocmos_rejected'
  )
  const historyRequests = requests.filter(r => r.status === 'director_approved' || r.status === 'director_rejected')

  const displayPending = useMemo(
    () => filterAndSortFkspRequests(pendingRequests, fkspFilterParams),
    [pendingRequests, fkspFilterParams],
  )
  const displayHistory = useMemo(
    () => filterAndSortFkspRequests(historyRequests, fkspFilterParams),
    [historyRequests, fkspFilterParams],
  )

  useEffect(() => {
    setPendingPage(0)
    setHistoryPage(0)
  }, [listSearch, listCreatedFrom, listCreatedTo, listSortField, listDateNewestFirst])

  const pagedPending = useMemo(
    () => fkspPageSlice(displayPending, pendingPage),
    [displayPending, pendingPage],
  )
  const pagedHistory = useMemo(
    () => fkspPageSlice(displayHistory, historyPage),
    [displayHistory, historyPage],
  )

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        FKSP - Panel Ředitele
      </Typography>
      <FkspRequestsToolbar
        search={listSearch}
        onSearchChange={setListSearch}
        createdFrom={listCreatedFrom}
        onCreatedFromChange={setListCreatedFrom}
        createdTo={listCreatedTo}
        onCreatedToChange={setListCreatedTo}
        sortField={listSortField}
        onSortFieldChange={setListSortField}
        dateNewestFirst={listDateNewestFirst}
        onDateSortChange={setListDateNewestFirst}
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" mb={3} color="primary">Žádosti čekající na finální rozhodnutí ({displayPending.length})</Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Datum</TableCell>
                  <TableCell>Žadatel</TableCell>
                  <TableCell>Typ / Účel</TableCell>
                  <TableCell align="right">Částka</TableCell>
                  <TableCell>Stanovisko odborů</TableCell>
                  <TableCell align="center">Akce</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>Žádné nové žádosti</TableCell></TableRow>
                ) : displayPending.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>Žádná žádost neodpovídá vyhledávání</TableCell></TableRow>
                ) : (
                  pagedPending.map(req => (
                    <TableRow
                      key={req.id}
                      hover
                      onClick={() => setSelectedReq(req)}
                      sx={{ cursor: 'pointer', ...fkspRowSxIfEconomProcessed(req) }}
                    >
                      <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{req.requester_name}</Typography>
                        <Typography variant="caption" color="text.secondary">Os. č: {req.personal_number}</Typography>
                      </TableCell>
                      <TableCell>
                        {req.type === 'benefity' ? 'Benefity' : 'Připojištění'}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {req.type === 'benefity' ? req.benefit_type : req.institution_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {req.type === 'benefity' ? req.amount : req.fksp_contribution} Kč
                      </TableCell>
                      <TableCell>
                        {req.financist_processed_at && (
                          <Chip label="Ekonom" size="small" sx={{ mr: 0.5, bgcolor: '#fff59d' }} />
                        )}
                        {req.zocmos_status === 'approved' ? <Chip label="Doporučeno" color="success" size="small"/> :
                         req.zocmos_status === 'rejected' ? <Chip label="Nedoporučeno" color="error" size="small"/> :
                         <Chip label="Bez vyjádření" size="small"/>}
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="contained" color="success" sx={{ mr: 1 }} onClick={(e) => { e.stopPropagation(); setSelectedReq(req) }}>Schválit</Button>
                        <Button size="small" variant="outlined" color="error" onClick={(e) => { e.stopPropagation(); setSelectedReq(req) }}>Zamítnout</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FkspTablePagination
            count={displayPending.length}
            page={pendingPage}
            onPageChange={setPendingPage}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={3}>Historie schválených a zamítnutých</Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Datum</TableCell>
                  <TableCell>Žadatel</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Rozhodnutí</TableCell>
                  <TableCell align="center">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      {historyRequests.length === 0 ? 'Žádná historie' : 'Žádná žádost neodpovídá vyhledávání'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedHistory.map(req => (
                  <TableRow
                    key={req.id}
                    hover
                    onClick={() => setSelectedReq(req)}
                    sx={{ cursor: 'pointer', ...fkspRowSxIfEconomProcessed(req) }}
                  >
                    <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                    <TableCell>{req.requester_name}</TableCell>
                    <TableCell>{req.type === 'benefity' ? 'Benefity' : 'Připojištění'}</TableCell>
                    <TableCell>
                      {req.financist_processed_at && (
                        <Chip label="Zpracováno ekonomem" size="small" sx={{ mr: 0.5, bgcolor: '#fff59d' }} />
                      )}
                      {req.director_status === 'approved' ? <Chip label="Schváleno" color="success" size="small"/> :
                       req.director_status === 'rejected' ? <Chip label="Zamítnuto" color="error" size="small"/> : '-'}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                        <Tooltip title="PDF pro tisk">
                          <IconButton color="secondary" size="small" onClick={() => handleDownloadPdf(req)}>
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FkspTablePagination
            count={displayHistory.length}
            page={historyPage}
            onPageChange={setHistoryPage}
          />
        </CardContent>
      </Card>

      <Snackbar open={toast.show} autoHideDuration={6000} onClose={() => setToast({ ...toast, show: false })}>
        <Alert severity={toast.type} onClose={() => setToast({ ...toast, show: false })}>{toast.msg}</Alert>
      </Snackbar>

      <FkspRequestModal
        open={selectedReq !== null}
        onClose={() => setSelectedReq(null)}
        request={selectedReq}
        canApprove={selectedReq?.director_status === 'pending' || selectedReq?.director_status == null}
        loading={actionLoading !== null}
        onApprove={(id, note) => {
          handleUpdateStatus(id, 'approve', note)
          setSelectedReq(null)
        }}
        onReject={(id, note) => {
          handleUpdateStatus(id, 'reject', note)
          setSelectedReq(null)
        }}
        onDelete={isItAdmin ? async (id) => {
          const ok = window.confirm('Opravdu chcete tuto žádost smazat? Žádost bude přesunuta do archivu smazaných (viditelného pouze správci IT).')
          if (!ok) return
          setActionLoading('approve')
          try {
            await deleteFkspRequest(id)
            setToast({ show: true, msg: 'Žádost byla smazána', type: 'success' })
            setSelectedReq(null)
            fetchRequests()
          } catch (err: any) {
            setToast({ show: true, msg: err.message || 'Chyba při mazání', type: 'error' })
          } finally {
            setActionLoading(null)
          }
        } : undefined}
        onDownloadPdf={async (req) => {
          try {
            await downloadFkspPdf(req.id, req.type)
          } catch (e) {
            setToast({ show: true, msg: 'Chyba PDF: ' + (e as Error).message, type: 'error' })
          }
        }}
      />
    </Box>
  )
}
